import store from "@/store";
import { refreshToken } from "@/@core/utils/refreshToken";
import router from "@/router";
import i18n from "@/i18n";
import { mixpanelWrapper } from "@/@core/libs/mixpanel";

/**
 * Supported auth actions
 */
const LOGIN = "login";
const LOGOUT = "logout";
const CHECK_AUTH = "checkAuth";
const CHECK_ERROR = "checkError";
const GET_NAME = "getName";
const GET_EMAIL = "getEmail";
const GET_PERMISSIONS = "getPermissions";
const refreshTokenFunc = refreshToken;

import FetchHydra from "@/@core/providers/fetch/fetchHydra";

export default (httpClient, params = {}) => {
  if (typeof httpClient === "string") {
    httpClient = new FetchHydra(httpClient);
  }

  params = {
    routes: {
      login: "api/auth/login",
      refresh: "api/token/refresh",
      logout: "api/auth/logout",
      user: "api/users/current",
    },
    getName: u => u.name,
    getEmail: u => u.email,
    getPermissions: u => u.roles,
    ...params,
  };

  let { routes, getName, getEmail, getPermissions } = params;

  return {
    [LOGIN]: async ({ email, password }) => {
      const loginData = {
        email,
        password,
      };

      let responseData;
      let response;

      try {
        //In case something breaks in inpersonation logic, remove any impersonation data before login
        store.commit("SET_USER_IMPERSONATE", null);
        store.commit("SET_ORIGINAL_USER", null);

        response = await fetch(`${process.env.VUE_APP_API}/${routes.login}`, {
          method: "POST",
          body: JSON.stringify(loginData),
          headers: {
            Accept: "application/ld+json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.status < 200 || response.status >= 300) {
          const result = await response.json();
          throw new Error(result["hydra:description"]);
        } else {
          responseData = await response.json();
        }
      } catch (e) {
        return Promise.reject(e);
      }

      mixpanelWrapper.identify(responseData?.user?.id);

      return Promise.resolve(responseData);
    },
    [LOGOUT]: async () => {
      try {
        store.commit("SET_USER_IMPERSONATE", null);
        store.commit("SET_ORIGINAL_USER", null);
        await httpClient.get(routes.logout);
      } catch (e) {
        // Fallback if await fails
        if (!store.state.skipLogoutRedirect) {
          window.location.assign("/login");
        }
      }

      store.commit("SET_USER_ID", "");
      mixpanelWrapper.reset();

      return Promise.resolve();
    },
    [CHECK_AUTH]: async () => {
      let userID = store.state.user.id;

      if (userID === "undefined") {
        userID = null;
      }

      return userID
        ? Promise.resolve({
            data: true,
          })
        : Promise.reject();
    },
    [CHECK_ERROR]: async ({ status, data }) => {
      //fallback conditional, because all checks on upper level
      if (status === 401) {
        await refreshTokenFunc();
      }

      if (status === 403) {
        const hydraError = data?.["hydra:error"];

        if (["invalid_subscription"].includes(hydraError)) {
          router.push({ name: "settings", query: { tab: "billing_payments" } });

          return Promise.resolve();
        }

        if (["insufficient_permissions"].includes(hydraError)) {
          setTimeout(() => {
            store.commit("messages/showToast", {
              color: "error",
              message: i18n.t("generic.error"),
              root: true,
            });
          }, 0);

          if (router.currentRoute.name !== "dashboard") {
            router.push({ name: "dashboard" });
          }

          return Promise.resolve();
        }

        if (["invalid_user_organization"].includes(hydraError)) {
          await store.dispatch("refreshAndStoreAllUserData");

          if (!store.state.currentlyActiveOrganization) {
            if (router.currentRoute.name !== "billing") {
              router.push({ name: "billing" });
            }
          }

          return Promise.resolve();
        }

        if (["not_confirmed_user_organization"].includes(hydraError)) {
          await store.dispatch("refreshAndStoreAllUserData");

          return Promise.resolve();
        }

        /**
         * Display "Access denied" toast.
         * Usually, whenever we're using httpClient, we wrap it inside of a try/catch block, and display an error toast in the catch block.
         * The problem is that in case of error happening, CHECK_ERROR is executed way before the catch block that is wrapping the httpClient call.
         * It means that even if we're trying to display an error toast here, it will be overriden by the error toast displayed in the catch block
         * (Because it's executed after CHECK_ERROR call)
         *
         * Solution to this is settimeout(() =>{}, 0), so that "Access Denied" message is displayed right after the callstack is cleared
         * and everything else is finished executing.
         */
        setTimeout(() => {
          store.commit("messages/showToast", {
            color: "error",
            message: i18n.t("generic.access_denied"),
            root: true,
          });
        }, 0);

        await store.dispatch("refreshAndStoreAllUserData");

        if (router.currentRoute.name !== "dashboard") {
          router.push({ name: "dashboard" });
        }

        return Promise.resolve();
      }

      return Promise.resolve();
    },
    [GET_NAME]: user => getName(user),
    [GET_EMAIL]: user => getEmail(user),
    [GET_PERMISSIONS]: user => getPermissions(user),
  };
};
